import { useMemo, useRef } from 'react'

import FeedbackIconProvider from '@/lib/provider/FeedbackIconProvider'
import ListMapShell from '@components/ListMapShell'
import StandaloneLoadingSpinner from '@components/StandaloneLoadingSpinner'
import ShelfDetails, { type FacingProducerOptions } from '@components/shelfDetails/ShelfDetails'
import StoreMap from '@components/storeMap/StoreMap'
import { usePageHeader } from '@hooks/useHeader'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useTodoListContext } from '@routes/TodoList/Context'
import NoResults from '@routes/TodoList/NoResults'
import TodoListProvider from '@routes/TodoList/Provider'
import SearchBar from '@routes/TodoList/SearchBar'
import TodoShelf from '@routes/TodoList/TodoShelf'
import { taskItemFacingProducer } from '@routes/TodoList/facingProducer'
import Todos from '@routes/TodoList/overlay/Todos'
import { usePlacementsByGtin } from '@state/product.ts'
import { useTodoCount } from '@state/todoList'

import TasksGraphic from './assets/tasks.svg?no-inline'

function TodoListRoute() {
  const { t } = useTypedTranslation()
  const { setSelectedShelfId, selectedShelfId, selectedTask } = useTodoListContext()
  const placements = usePlacementsByGtin(selectedTask?.product.gtin)
  const feedbackPortalRef = useRef<HTMLDivElement>(null)

  const facingProducer = useMemo(
    () => selectedTask
      ? (options: FacingProducerOptions) => taskItemFacingProducer({ task: selectedTask, ...options })
      : undefined,
    [selectedTask]
  )

  const { countCompleted, countOpen } = useTodoCount()

  usePageHeader({ title: t('todoList.title') })

  if (countOpen === undefined) return <StandaloneLoadingSpinner />
  if (countOpen === 0 && countCompleted === 0) {
    return <NoResults illustration={TasksGraphic} message={t('todoList.emptyStates.noTasksToday')} />
  }

  return (
    <div className='flex h-full flex-col'>
      <FeedbackIconProvider portalContainer={feedbackPortalRef}>
        <ListMapShell
          search={<SearchBar />}
          map={
            <div ref={feedbackPortalRef} className='relative h-full p-4'>
              <StoreMap
                showToolbar={true}
                selectedShelfId={selectedShelfId}
                setSelectedShelfId={setSelectedShelfId}
                highlightedShelves={placements}
                overlays3D={[
                  <Todos key="todo-overlay" />,
                ]}
                shelfProducer2D={
                  (shelf) => <TodoShelf key={shelf.id} shelf={shelf} />
                }
              />
            </div>
          }
        />
      </FeedbackIconProvider>

      <ShelfDetails
        shelfId={selectedShelfId}
        onShelfIdChange={setSelectedShelfId}
        facingProducer={facingProducer}
      />
    </div>
  )
}

function WrappedTodoListRoute() {
  return (
    <TodoListProvider>
      <TodoListRoute />
    </TodoListProvider>
  )
}

export default WrappedTodoListRoute